import { useState, useEffect } from "react";
import { Phone, Mail, MapPin, Download, ArrowRight, MessageCircle, Clock, GraduationCap, Award, Navigation, ShieldCheck, Heart, Leaf } from "lucide-react";
import { Link } from "react-router-dom";
import { TEAM, NEWS } from "../data/praxis";
import { downloadVCF } from "../utils/vcf";
import { hasConsent, setConsent, getConsent } from "../utils/consent";
import AutoFitText from "../components/AutoFitText";
import NewsBanner from "../components/NewsBanner";
import { api } from "../utils/api";
import { usePraxis } from "../context/PraxisContext";
import { getIconComponent } from "../data/icons";

const ICONS = {}; // Legacy — nicht mehr genutzt, Icons kommen jetzt aus data/icons.js

export default function Home() {
  const { settings: PRAXIS, hours: SPRECHZEITEN, services: LEISTUNGEN, about: ABOUT } = usePraxis();
  const MAPS_KEY = "praxis_maps_clicked_v1";
  const [mapsAllowed, setMapsAllowed] = useState(false);
  // Team dynamisch aus dem CMS; Fallback: statische Liste aus praxis.js
  const [teamMembers, setTeamMembers] = useState(
    TEAM.map((m) => ({ name: m.name, role: m.role, img: m.img }))
  );

  useEffect(() => {
    if (localStorage.getItem(MAPS_KEY) === "1") setMapsAllowed(true);
  }, []);

  useEffect(() => {
    let alive = true;
    api.getPublicTeam().then(({ data }) => {
      if (!alive) return;
      if (data && data.ok && Array.isArray(data.items) && data.items.length > 0) {
        setTeamMembers(
          data.items.map((it) => ({
            name: it.name,
            role: it.position,
            img: it.image_url || "/team/placeholder.svg",
          }))
        );
      }
    }).catch(() => { /* Fallback bleibt */ });
    return () => { alive = false; };
  }, []);

  const enableMaps = () => {
    localStorage.setItem(MAPS_KEY, "1");
    setMapsAllowed(true);
    // zusätzlich: Maps-Consent im Cookie-Store setzen (für Konsistenz)
    const c = getConsent() || { necessary: true };
    setConsent({ ...c, maps: true });
  };

  return (
    <>
      {/* HERO */}
      <section className="hero" aria-label="Willkommen">
        <div className="hero-inner">
          <div>
            <p className="hero-eyebrow">Gynäkologie & Geburtshilfe · Bürstadt</p>
            <h1>
              Medizin mit <em>Herz</em> –<br />
              für jede Frau
            </h1>
            <p className="hero-lead">
              Als Ihre Frauenärztin sehe und behandle ich Sie ganzheitlich – mit Zeit,
              Empathie und über 20 Jahren Erfahrung. Ob Vorsorge, Schwangerschaft,
              Wechseljahre oder Krebsnachsorge: Sie finden bei mir immer ein offenes Ohr.
            </p>
            <div className="hero-actions">
              <a
                href={`tel:${PRAXIS.phoneRaw}`}
                className="btn btn-gold"
                data-testid="hero-anrufen-btn"
              >
                <Phone size={16} /> Anrufen
              </a>
              <a href="#leistungen" className="btn btn-outline" data-testid="hero-leistungen-btn">
                Leistungen ansehen
              </a>
            </div>
          </div>

          {/* Freigestelltes Portrait */}
          <div className="hero-portrait" data-testid="hero-portrait">
            <img
              src={ABOUT.hero_photo || "/team/dr-scheer-freigestellt.png"}
              alt="Dr. med. Inka Scheer – Fachärztin für Gynäkologie und Geburtshilfe"
              onError={(e) => { e.currentTarget.src = '/team/dr-scheer.jpg'; }}
            />
          </div>
        </div>
      </section>

      {/* Dynamischer News-Banner (nur sichtbar wenn Admin etwas eingetragen hat) */}
      <NewsBanner />

      {/* KONTAKT-BAR direkt unter dem Hero */}
      <div className="kontakt-bar" data-testid="kontakt-bar">
        <div className="kontakt-bar-inner">
          <div className="kontakt-bar-item">
            <div className="ico"><MapPin size={18} /></div>
            <div>
              <span className="label">Adresse</span>
              <span className="value">{PRAXIS.street}<br />{PRAXIS.zip} {PRAXIS.city}</span>
            </div>
          </div>
          <div className="kontakt-bar-item">
            <div className="ico"><Phone size={18} /></div>
            <div>
              <span className="label">Telefon</span>
              <a href={`tel:${PRAXIS.phoneRaw}`} className="value">{PRAXIS.phone}</a>
            </div>
          </div>
          <div className="kontakt-bar-item">
            <div className="ico"><Mail size={18} /></div>
            <div>
              <span className="label">E-Mail</span>
              <a href={`mailto:${PRAXIS.email}`} className="value">{PRAXIS.email}</a>
            </div>
          </div>
          <button
            className="btn btn-primary vcf-btn"
            onClick={() => downloadVCF(PRAXIS)}
            data-testid="vcf-download-btn"
          >
            <Download size={16} /> Kontakt speichern
          </button>
        </div>
      </div>

      {/* PHILOSOPHIE */}
      <section className="section section-leistungen" id="philosophie" data-testid="philosophie-section">
        <div className="section-inner">
          <div className="section-head">
            <p className="section-label">Meine Philosophie</p>
            <h2 className="section-title">
              Die Frau <em>ganzheitlich</em> sehen
            </h2>
          </div>

          <div className="philo-intro">
            <p>
              Jede Frau ist einzigartig – mit ihrer Geschichte, ihren Beschwerden, ihren
              Ängsten und ihren Stärken. Mein tiefer Wunsch ist es, jede Patientin ganzheitlich
              zu sehen und mit Empathie zu begleiten – das gilt für jede Frau, die meine
              Praxis betritt, unabhängig davon, ob sie gesetzlich oder privat versichert ist.
            </p>
            <p>
              Ich bin mir bewusst, dass der Praxisalltag im deutschen Gesundheitssystem wenig
              Raum für die Zeit lässt, die manche Themen wirklich verdienen. Ich tue alles
              dafür, auch in der regulären Sprechstunde so individuell und aufmerksam wie
              möglich zu sein – und jede Patientin als Mensch wahrzunehmen, nicht nur als Fall.
            </p>
            <p>
              Gleichzeitig weiß ich, dass es Themen gibt – allen voran die Wechseljahre – die
              einfach mehr brauchen: mehr Zeit, mehr Tiefe, mehr Individualität. Für genau
              diese Situationen habe ich die private Videosprechstunde ins Leben gerufen. Sie
              richtet sich an Frauen, die sich mehr Flexibilität wünschen – mit mehr Raum für
              ihr Anliegen, in vertrauter Umgebung und ganz ohne Zeitdruck.
            </p>
          </div>

          <div className="philo-grid">
            <article className="philo-card" data-testid="philo-empathie">
              <div className="ico"><Heart size={20} strokeWidth={1.6} /></div>
              <h3>Empathie & Vertrauen</h3>
              <p>
                Bei mir können Sie offen über alles sprechen – ohne Zeitdruck und
                ohne Scham. Ich höre zu und nehme Ihre Anliegen ernst.
              </p>
            </article>
            <article className="philo-card" data-testid="philo-zeit">
              <div className="ico"><Clock size={20} strokeWidth={1.6} /></div>
              <h3>Zeit für Sie</h3>
              <p>
                In der privaten Videosprechstunde nehme ich mir die Zeit, die Sie
                wirklich brauchen – gerade für komplexe Themen wie Wechseljahre.
              </p>
            </article>
            <article className="philo-card" data-testid="philo-ganzheitlich">
              <div className="ico"><Leaf size={20} strokeWidth={1.6} /></div>
              <h3>Ganzheitliche Medizin</h3>
              <p>
                Ich behandle nicht nur Symptome, sondern sehe die Frau in ihrer
                Gesamtheit – körperlich, seelisch und in ihrem Umfeld.
              </p>
            </article>
            <article className="philo-card" data-testid="philo-wissenschaft">
              <div className="ico"><GraduationCap size={20} strokeWidth={1.6} /></div>
              <h3>Wissenschaft & Erfahrung</h3>
              <p>
                Über 20 Jahre klinische Erfahrung, internationale Stationen und
                wissenschaftliche Publikationen bilden die Grundlage meiner Arbeit.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* LEISTUNGEN */}
      <section className="section section-cream" id="leistungen" data-testid="leistungen-section">
        <div className="section-inner">
          <div className="section-head">
            <h2 className="section-title">
              Unsere <em>Leistungen</em>
            </h2>
            <p className="lead">
              Ein umfassendes Angebot frauenärztlicher Versorgung – persönlich, am Puls
              der Forschung und auf Sie zugeschnitten.
            </p>
          </div>

          <div className="leistungen-grid">
            {LEISTUNGEN.map((l) => {
              const Icon = getIconComponent(l.icon);
              return (
                <article key={l.slug || l.id} className="leistung-card" data-testid={`leistung-${l.slug}`}>
                  {l.is_new && (
                    <span className="leistung-new-badge" data-testid={`leistung-new-${l.slug}`}>
                      {l.new_label || "Neu"}
                    </span>
                  )}
                  <div className="leistung-icon">
                    <Icon size={22} strokeWidth={1.5} />
                  </div>
                  <h3>{l.title}</h3>
                  <p>{l.short}</p>
                  {l.bullets && l.bullets.length > 0 && (
                    <ul>
                      {l.bullets.map((b, i) => (
                        <li key={i}>{b}</li>
                      ))}
                    </ul>
                  )}
                  {l.note && (
                    <p className="leistung-note" data-testid={`leistung-note-${l.slug}`}>
                      {l.note}
                    </p>
                  )}
                  {l.has_page && l.page_path && (
                    <Link to={`/${l.page_path}`} className="more" data-testid={`leistung-link-${l.slug}`}>
                      Mehr erfahren <ArrowRight size={14} />
                    </Link>
                  )}
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* ÜBER MICH */}
      <section className="section" id="ueber-mich" data-testid="ueber-mich-section">
        <div className="section-inner">
          <div className="about-grid">
            <div className="about-photo-wrap">
              <div className="about-photo">
                <img
                  src={ABOUT.photo || "/team/dr-scheer.jpg"}
                  alt="Dr. med. Inka Scheer – Fachärztin für Gynäkologie und Geburtshilfe"
                  loading="lazy"
                  onError={(e) => { e.currentTarget.src = "/team/dr-scheer.jpg"; }}
                />
              </div>
              {(ABOUT.badge_number || ABOUT.badge_text) && (
                <div className="about-badge">
                  <strong>{ABOUT.badge_number}</strong>
                  <span>{ABOUT.badge_text}</span>
                </div>
              )}
            </div>

            <div className="about-content">
              {ABOUT.label && <p className="section-label">{ABOUT.label}</p>}
              <h2>
                {ABOUT.name_pre} <em>{ABOUT.name_em}</em>
              </h2>
              {ABOUT.intro && (
                <p style={{ whiteSpace: "pre-line" }}>{ABOUT.intro}</p>
              )}

              <div className="timeline">
                {ABOUT.career && ABOUT.career.length > 0 && (
                  <>
                    <h3>{ABOUT.career_heading || "Beruflicher Werdegang"}</h3>
                    {ABOUT.career.map((c, i) => (
                      <div key={`career-${i}`} className="timeline-item">
                        <span className="timeline-year">{c.years}</span>
                        <span className="timeline-text">
                          {c.title && <strong>{c.title}</strong>}
                          {c.text}
                        </span>
                      </div>
                    ))}
                  </>
                )}

                {ABOUT.awards && ABOUT.awards.length > 0 && (
                  <>
                    <h3 style={{ marginTop: "2rem" }}>
                      <Award size={16} style={{ verticalAlign: "-3px", marginRight: "0.4rem", color: "var(--gold-dark)" }} />
                      {ABOUT.awards_heading || "Auszeichnungen (Auswahl)"}
                    </h3>
                    {ABOUT.awards.map((a, i) => (
                      <div key={`award-${i}`} className="timeline-item">
                        <span className="timeline-year">{a.year}</span>
                        <span className="timeline-text">{a.text}</span>
                      </div>
                    ))}
                  </>
                )}

                {ABOUT.memberships && ABOUT.memberships.length > 0 && (
                  <>
                    <h3 style={{ marginTop: "2rem" }}>{ABOUT.memberships_heading || "Mitgliedschaften"}</h3>
                    <div className="tags">
                      {ABOUT.memberships.map((m, i) => (
                        <span key={`mem-${i}`} className="tag">{m}</span>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TEAM */}
      <section className="section section-cream" id="team" data-testid="team-section">
        <div className="section-inner">
          <div className="section-head">
            <p className="section-label">Wir freuen uns auf Sie</p>
            <h2 className="section-title">
              Unser <em>Team</em>
            </h2>
            <p className="lead">
              Vier engagierte medizinische Fachangestellte – mit Herz, Erfahrung und
              einem offenen Ohr für Sie.
            </p>
          </div>

          <div className="team-grid">
            {teamMembers.map((m, i) => (
              <article key={i} className="team-card" data-testid={`team-member-${i}`}>
                <div className="team-photo">
                  <img
                    src={m.img || "/team/placeholder.svg"}
                    alt={m.name}
                    loading="lazy"
                    onError={(e) => { e.currentTarget.src = "/team/placeholder.svg"; }}
                  />
                </div>
                <h3 className="team-name">{m.name}</h3>
                <p className="team-role">{m.role}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* NEU IN DER PRAXIS - vereinfacht */}
      <section className="section neu-section" data-testid="neu-section">
        <div className="section-inner">
          <div className="neu-card">
            <span className="badge">{NEWS.badge}</span>
            <h2>{NEWS.title}</h2>
            <p>{NEWS.text}</p>
            {NEWS.bullets && (
              <ul className="neu-bullets">
                {NEWS.bullets.map((b, i) => (
                  <li key={i}>{b}</li>
                ))}
              </ul>
            )}
            <Link to={NEWS.link} className="btn btn-gold" data-testid="neu-mehr-btn">
              {NEWS.cta} <ArrowRight size={16} />
            </Link>
            {NEWS.note && (
              <p className="neu-note" data-testid="neu-note">{NEWS.note}</p>
            )}
          </div>
        </div>
      </section>

      {/* KONTAKT */}
      <section className="section" id="kontakt" data-testid="kontakt-section">
        <div className="section-inner">
          <div className="section-head">
            <p className="section-label">Wir freuen uns auf Sie</p>
            <h2 className="section-title">
              Kontakt & <em>Anfahrt</em>
            </h2>
          </div>

          <div className="kontakt-grid">
            <div>
              <div className="kontakt-block">
                <h3>Adresse</h3>
                <p>
                  {PRAXIS.name}<br />
                  {PRAXIS.street}<br />
                  {PRAXIS.zip} {PRAXIS.city}
                </p>
              </div>

              <div className="kontakt-block">
                <h3>Telefon & E-Mail</h3>
                <a href={`tel:${PRAXIS.phoneRaw}`}>Tel: {PRAXIS.phone}</a>
                <a href={`tel:${PRAXIS.faxRaw}`}>Fax: {PRAXIS.fax}</a>
                <a href={`mailto:${PRAXIS.email}`}>{PRAXIS.email}</a>
                <a href={PRAXIS.webUrl} target="_blank" rel="noopener noreferrer">{PRAXIS.web}</a>
              </div>

              <div className="kontakt-block">
                <h3>Sprechzeiten</h3>
                <div className="oeffnung-grid">
                  {SPRECHZEITEN.map((s, i) => (
                    <span key={s.id ?? i} style={{ display: "contents" }}>
                      <span className="oeffnung-day">{s.day}</span>
                      <span className="oeffnung-time" style={{ whiteSpace: "pre-line" }}>{s.time}</span>
                    </span>
                  ))}
                </div>
                <p style={{ fontSize: ".82rem", color: "var(--text-light)", marginTop: "0.85rem", lineHeight: 1.65 }}>
                  Bitte sprechen Sie Wunschtermine bei der Vereinbarung an, damit
                  wir ausreichend Zeit einplanen können.
                </p>
              </div>

              <button
                className="btn btn-primary"
                onClick={() => downloadVCF(PRAXIS)}
                data-testid="kontakt-vcf-btn"
              >
                <Download size={16} /> Kontakt speichern
              </button>
            </div>

            <div>
              <div className="map-box" data-testid="map-box">
                {mapsAllowed ? (
                  <iframe
                    className="map-frame"
                    title="Standort der Praxis Dr. Scheer auf der Karte"
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    src={`https://maps.google.com/maps?q=${encodeURIComponent(PRAXIS.street + ", " + PRAXIS.zip + " " + PRAXIS.city)}&hl=de&z=16&output=embed`}
                  />
                ) : (
                  <div className="map-placeholder" data-testid="map-placeholder">
                    <ShieldCheck size={42} strokeWidth={1.5} />
                    <h3>Karte aktivieren</h3>
                    <p>
                      Beim Anzeigen der Karte werden Daten an Google übertragen und
                      Cookies gesetzt. Sind Sie damit einverstanden?
                    </p>
                    <button
                      className="btn btn-gold"
                      onClick={enableMaps}
                      data-testid="map-enable-btn"
                    >
                      Karte anzeigen
                    </button>
                    <p className="map-placeholder-note">
                      Sie können die Zustimmung jederzeit in den{" "}
                      <Link to="/datenschutz">Datenschutz-Einstellungen</Link> widerrufen.
                    </p>
                  </div>
                )}
                <div className="map-footer">
                  <div className="addr">
                    <strong>{PRAXIS.name}</strong>
                    {PRAXIS.street} · {PRAXIS.zip} {PRAXIS.city}
                  </div>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(PRAXIS.street + ", " + PRAXIS.zip + " " + PRAXIS.city)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline-gold"
                    data-testid="maps-link"
                  >
                    <Navigation size={16} /> Route
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
